import React, { useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import {
    TextField,
    Button,
    Snackbar,
    Alert,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
} from "@mui/material";

import { useRegisteredUserContactSubmission } from "../../hooks/appHooks";

const RegisteredUserContactSchema = Yup.object().shape({
    subject: Yup.string()
        .required("Subject is required")
        .max(200, "Subject is too long"),
    message: Yup.string()
        .required("Message is required")
        .min(10, "Message is too short")
        .max(1000, "Message is too long"),
    phone_number: Yup.string()
        .matches(/^[0-9\-+()\s]*$/, "Phone number is not valid")
        .max(15, "Phone number is too long"),
    priority: Yup.string()
        .required("Priority is required")
        .oneOf(["Low", "Medium", "High", "Urgent"], "Invalid priority"),
});


const RegisteredUserContactForm = ({ user }) => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const onSuccess = () => {
        setSnackbarMessage("Your message has been sent successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
    };

    const onFailure = (error) => {
        setSnackbarMessage(error.message || "An error occurred. Please try again later.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
    };

    const {
        mutate: registeredUserContactSubmission,
        isLoading: isSubmitting
    } =
        useRegisteredUserContactSubmission({
            onSuccess,
            onFailure,
        });

    const handleFormSubmit = (values, { resetForm }) => {
        registeredUserContactSubmission(values, {
            onSuccess: () => resetForm(),
        });
    };

    return (
        <Formik
            initialValues={{
                subject: "",
                message: "",
                phone_number: "",
                priority: "Low",
            }}
            validationSchema={RegisteredUserContactSchema}
            onSubmit={(values, { resetForm }) => {
                // console.log("Formik submitted values:", values);
                handleFormSubmit(values, { resetForm });
                // setSubmitting(false);
            }}
        >
            {({ values, errors, touched, handleChange, handleBlur }) => (
                <Form noValidate autoComplete="off">
                    <FormControl fullWidth margin="normal">
                        <TextField
                            id="subject"
                            name="subject"
                            label="Subject"
                            variant="outlined"
                            size="small"
                            value={values.subject}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting}
                            error={touched.subject && Boolean(errors.subject)}
                            helperText={touched.subject && errors.subject}
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <TextField
                            id="message"
                            name="message"
                            label="Message"
                            variant="outlined"
                            size="small"
                            multiline
                            rows={4}
                            value={values.message}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting}
                            error={touched.message && Boolean(errors.message)}
                            helperText={touched.message && errors.message}
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <TextField
                            id="phone_number"
                            name="phone_number"
                            label="Phone Number (Optional)"
                            variant="outlined"
                            size="small"
                            value={values.phone_number}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting}
                            error={touched.phone_number && Boolean(errors.phone_number)}
                            helperText={touched.phone_number && errors.phone_number}
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <InputLabel id="priority-label">Priority</InputLabel>
                        <Select
                            id="priority"
                            name="priority"
                            labelId="priority-label"
                            size="small"
                            value={values.priority}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting}
                            error={touched.priority && Boolean(errors.priority)}
                        >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Urgent">Urgent</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        fullWidth
                        sx={{ mt: 2, padding: "8px 0" }}
                    >
                        Submit
                    </Button>

                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={3000}
                        onClose={() => setSnackbarOpen(false)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    >
                        <Alert
                            onClose={() => setSnackbarOpen(false)}
                            severity={snackbarSeverity}
                            sx={{ width: "100%" }}
                        >
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </Form>
            )}
        </Formik>
    );
};

export default RegisteredUserContactForm;
